<template>
  <div class="res-page">

    <!-- ── PAGE HEADER ── -->
    <div class="page-header">
      <div class="page-header__left">
        <span class="page-header__eyebrow">Property Management</span>
        <h1 class="page-header__title">Reservations</h1>
      </div>
      <div class="page-header__right">
        <a
          v-if="userDetails.roles && userDetails.roles[0] && userDetails.roles[0].name === 'Admin'"
          class="btn btn-outline-secondary btn-sm"
          href="/reservation-dashboard"
        >
          <i class="bi bi-bar-chart"></i> Dashboard
        </a>
        <a class="btn btn-primary btn-sm" href="/make-reservations">
          <i class="bi bi-plus"></i> New Reservation
        </a>
      </div>
    </div>

    <!-- ── FILTER BAR ── -->
    <div class="filter-bar">
      <div class="filter-bar__inner">
        <div class="filter-slot filter-slot--date">
          <date-range-picker
            v-model="dateRange"
            :auto-apply="autoApply"
            :ranges="ranges"
            :opens="datepicker_opens"
            :append-to-body="append_to_body"
          >
            <template #input="picker">
              <div class="date-picker-trigger">
                <i class="bi bi-calendar3"></i>
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate,'DD MMM') }} — {{ formatDate(picker.endDate,'DD MMM YYYY') }}
                </span>
                <span v-else class="placeholder">All dates</span>
              </div>
            </template>
          </date-range-picker>
        </div>
        <div class="filter-slot">
          <select v-model="filters.payment_status" class="filter-select">
            <option value="">All Payments</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partial</option>
          </select>
        </div>
        <div class="filter-slot filter-slot--search">
          <input
            v-model="filters.search"
            type="text"
            class="filter-select search-input"
            placeholder="Search guest, room, ref…"
            @input="onSearchInput"
          />
        </div>
        <button class="btn btn-outline-secondary btn-sm" @click="fetchReservations">
          <i class="bi bi-sliders"></i> Apply
        </button>
      </div>
    </div>

    <!-- ── RESERVATIONS TABLE ── -->
    <div class="table-wrap">
      <table class="res-table">
        <thead>
          <tr>
            <th>Ref #</th>
            <th>Guest</th>
            <th>Room</th>
            <th>Status</th>
            <th>Amount</th>
            <th>Check-in</th>
            <th>Check-out</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="res in reservations"
            :key="res.id"
            class="res-row"
            @click="viewReservation(res.id)"
          >
            <td class="cell-ref">{{ res.reservation_number || '—' }}</td>
            <td class="cell-guest">
              <span class="guest-avatar">{{ guestInitial(res.customer) }}</span>
              {{ res.customer?.full_name || '—' }}
            </td>
            <td class="cell-room">{{ res.product?.product || res.room?.product || '—' }}</td>
            <td class="cell-status">
              <span class="status-chip" :class="`status-chip--${res.status}`">
                {{ res.status || 'unknown' }}
              </span>
            </td>
            <td class="cell-amount">{{ convertCurrencyToLocal(res.total_amount || 0) }}</td>
            <td class="cell-date cell-date--in">{{ formatDate(res.check_in,'DD MMM YYYY') }}</td>
            <td class="cell-date cell-date--out">{{ formatDate(res.check_out,'DD MMM YYYY') }}</td>
          </tr>
          <tr v-if="!reservations.length">
            <td colspan="7" class="empty-state">
              <i class="bi bi-building"></i>
              <span>No reservations found</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ══════════════════════════════════════════════════════════
         VIEW RESERVATION MODAL
         ══════════════════════════════════════════════════════ -->
    <div class="modal fade" id="reservationModal" tabindex="-1" role="dialog"
      data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-dialog-centered modal-xl">
        <div class="modal-content res-modal">

          <!-- Loading -->
          <div v-if="loadingReservationDetails" class="modal-loading">
            <div class="spinner-ring"></div>
            <p>Loading reservation…</p>
          </div>

          <template v-else-if="reservation && reservation.reservation">

            <!-- Modal header -->
            <div class="res-modal__header">
              <div class="res-modal__header-left">
                <span class="res-modal__eyebrow">Reservation</span>
                <h2 class="res-modal__number">#{{ reservation.reservation.reservation_number }}</h2>
              </div>
              <div class="res-modal__header-right">
                <span class="status-chip status-chip--lg" :class="`status-chip--${reservation.status}`">
                  {{ reservation.status }}
                </span>
                <div class="action-group">
                  <button class="icon-btn" title="Print 80mm receipt" @click="printThermalReceipt">
                    <i class="bi bi-printer"></i>
                  </button>
                  <button class="icon-btn" title="Download PDF (A4)" @click="downloadPdfReceipt">
                    <i class="bi bi-file-pdf"></i>
                  </button>
                  <div class="dropdown">
                    <button class="icon-btn" data-toggle="dropdown" title="More actions">
                      <i class="bi bi-three-dots-vertical"></i>
                    </button>
                    <div class="dropdown-menu dropdown-menu-right res-dropdown">
                      <div class="dropdown-section-label">Reservation</div>
                      <a class="dropdown-item" href="#" @click.prevent="checkInReservation(reservation.reservation.id)">
                        <i class="bi bi-box-arrow-in-right"></i> Check-in
                      </a>
                      <a class="dropdown-item" href="#" @click.prevent="checkOutReservation(reservation.reservation.id)">
                        <i class="bi bi-box-arrow-right"></i> Check-out
                      </a>
                      <a class="dropdown-item" href="#" @click.prevent="extendReservation(reservation.reservation.id)">
                        <i class="bi bi-calendar-plus"></i> Extend
                      </a>
                      <a class="dropdown-item" href="#" @click.prevent="transferReservation(reservation.reservation.id)">
                        <i class="bi bi-arrow-left-right"></i> Transfer
                      </a>
                      <a class="dropdown-item text-danger" href="#" @click.prevent="cancelReservation(reservation.reservation.id)">
                        <i class="bi bi-x-circle"></i> Cancel
                      </a>
                      <div class="dropdown-divider"></div>
                      <div class="dropdown-section-label">Billing</div>
                      <a class="dropdown-item" href="#" @click.prevent="openAttachOrderModal(reservation.reservation.id)">
                        <i class="bi bi-link-45deg"></i> Attach Order
                      </a>
                      <a class="dropdown-item" href="#" @click.prevent="addPaymentReservation(reservation.reservation.id)">
                        <i class="bi bi-credit-card"></i> Add Payment
                      </a>
                      <a
                        v-if="canGenerateFiscalReceipt"
                        class="dropdown-item"
                        href="#"
                        @click.prevent="generateFiscalReceiptReservation(reservation.reservation.id)"
                      >
                        <i class="bi bi-receipt"></i>
                        {{ reservation.reservation.fiscal_transaction && reservation.reservation.fiscal_transaction.status === 'failed' ? 'Retry Fiscal Receipt' : 'Generate Fiscal Receipt' }}
                      </a>
                      <a
                        v-if="canIssueFiscalCreditNote"
                        class="dropdown-item"
                        href="#"
                        @click.prevent="fiscalCreditNoteReservation(reservation.reservation.id)"
                      >
                        <i class="bi bi-receipt-cutoff"></i> Generate Fiscal Credit Note
                      </a>
                      <a
                        v-if="canDownloadCreditNote"
                        class="dropdown-item text-danger"
                        href="#"
                        @click.prevent="downloadCreditNotePdf"
                      >
                        <i class="bi bi-file-earmark-minus"></i> Download Credit Note
                      </a>
                    </div>
                  </div>
                  <button class="icon-btn icon-btn--close" data-dismiss="modal" title="Close">
                    <i class="bi bi-x"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Stay strip -->
            <div class="stay-strip">
              <div class="text-dark">
                <span>Room / House</span>
                <span class="text-primary">
                  {{ reservation.room?.product || '—' }}
                  <template v-if="reservation.child_rooms && reservation.child_rooms.length">
                    <span v-for="cr in reservation.child_rooms" :key="cr.id" class="ms-1">
                      · {{ cr.product?.product }}
                    </span>
                  </template>
                </span>
              </div>
              <div class="stay-strip__sep"><i class="bi bi-arrow-right"></i></div>
              <div class="text-dark">
                <span >Check-in</span>
                <span class="text-primary">{{ formatDate(reservation.reservation.check_in,'DD MMM YYYY') }}</span>
              </div>
              <div class="stay-strip__sep">·</div>
              <div class="text-dark">
                <span >Check-out</span>
                <span class="text-primary">{{ formatDate(reservation.reservation.check_out,'DD MMM YYYY') }}</span>
              </div>
              <div class="stay-strip__sep">·</div>
              <div class="text-dark">
                <span class="stay-strip__label">Nights</span>
                <span class="text-primary text-primary--accent">{{ nightsCount }}</span>
              </div>
              <div class="stay-strip__sep">·</div>
              <div class="text-dark">
                <span >Guests</span>
                <span class="text-primary">
                  {{ reservation.guests?.adults || 0 }} adults
                  <template v-if="reservation.guests?.children">
                    · {{ reservation.guests.children }} children
                  </template>
                </span>
              </div>
            </div>

            <!-- Modal body -->
            <div class="res-modal__body">

              <!-- LEFT: Guest + Orders -->
              <div class="res-modal__main">

                <!-- Guest card -->
                <div class="detail-card">
                  <div class="detail-card__header">
                    <span class="detail-card__label">Guest</span>
                  </div>
                  <div class="guest-block">
                    <div class="guest-block__avatar">
                      {{ guestInitial(reservation.reservation.customer) }}
                    </div>
                    <div class="guest-block__info">
                      <div class="guest-block__name">
                        {{ reservation.reservation.customer?.full_name || '—' }}
                      </div>
                      <div class="guest-block__meta" v-if="reservation.reservation.customer?.phone">
                        <i class="bi bi-telephone"></i>
                        {{ reservation.reservation.customer.phone }}
                      </div>
                      <div class="guest-block__meta" v-if="reservation.reservation.customer?.email">
                        <i class="bi bi-envelope"></i>
                        {{ reservation.reservation.customer.email }}
                      </div>
                      <div class="guest-block__meta" v-if="reservation.reservation.customer?.krapin">
                        <i class="bi bi-fingerprint"></i>
                        PIN: {{ reservation.reservation.customer.krapin }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Orders card -->
                <div class="detail-card">
                  <div class="detail-card__header">
                    <span class="detail-card__label">Attached Orders</span>
                    <button class="btn btn-link btn-sm p-0 text-secondary" @click="openAttachOrderModal(reservation.reservation.id)">
                      <i class="bi bi-plus"></i> Attach
                    </button>
                  </div>
                  <div v-if="reservation.orders && reservation.orders.length" class="orders-list">
                    <div
                      v-for="order in reservation.orders"
                      :key="order.id"
                      class="order-row"
                    >
                      <div class="order-row__left" @click="openOrderDetails(order.id)" style="cursor:pointer">
                        <span class="order-row__number">{{ order.order }}</span>
                        <span class="order-row__date">{{ formatDate(order.created_at,'HH:mm · DD MMM YYYY') }}</span>
                      </div>
                      <div class="order-row__right">
                        <span class="order-row__amount">{{ convertCurrencyToLocal(order.total_amount) }}</span>
                        <span class="order-chip"
                          :class="order.status === 1 ? 'order-chip--cleared' : order.partial ? 'order-chip--partial' : 'order-chip--unpaid'">
                          {{ order.status === 1 ? 'Cleared' : order.partial ? 'Partial' : 'Unpaid' }}
                        </span>
                        <button class="detach-btn"
                          @click="detachOrderFromReservation(reservation.reservation.id, order.id)"
                          title="Detach order">
                          <i class="bi bi-link-break"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <p v-else class="empty-text">No orders attached yet.</p>
                </div>

              </div>

              <!-- RIGHT: Payment + Stats -->
              <div class="res-modal__aside">

                <!-- Payment card -->
                <div class="detail-card detail-card--payment">
                  <div class="detail-card__header">
                    <span class="detail-card__label">Payment</span>
                  </div>
                  <div class="payment-stack">
                    <div class="payment-row">
                      <span class="payment-row__label">Total</span>
                      <span class="payment-row__value">{{ convertCurrencyToLocal(reservation.total_amount) }}</span>
                    </div>
                    <div class="payment-row payment-row--paid">
                      <span class="payment-row__label">Paid</span>
                      <span class="payment-row__value">{{ convertCurrencyToLocal(reservation.amount_paid) }}</span>
                    </div>
                    <div class="payment-divider"></div>
                    <div class="payment-row payment-row--balance">
                      <span class="payment-row__label">Balance</span>
                      <span class="payment-row__value payment-row__value--big">
                        {{ convertCurrencyToLocal(reservation.balance) }}
                      </span>
                    </div>
                  </div>
                  <button class="btn btn-primary btn-sm btn-block mt-2 mb-2 mx-3" style="width:calc(100% - 28px)" @click="addPaymentReservation(reservation.reservation.id)">
                    <i class="bi bi-credit-card"></i> Add Payment
                  </button>
                </div>

                <!-- Stat chips -->
                <div class="stat-chips">
                  <div class="stat-chip">
                    <span class="stat-chip__val">{{ nightsCount }}</span>
                    <span class="stat-chip__lbl">nights</span>
                  </div>
                  <div class="stat-chip">
                    <span class="stat-chip__val">{{ reservation.guests?.adults || 0 }}</span>
                    <span class="stat-chip__lbl">adults</span>
                  </div>
                  <div class="stat-chip">
                    <span class="stat-chip__val">{{ reservation.orders?.length || 0 }}</span>
                    <span class="stat-chip__lbl">orders</span>
                  </div>
                </div>

              </div>
            </div>

          </template>
        </div>
      </div>
    </div>

    <!-- ── ATTACH ORDER MODAL ── -->
    <div class="modal fade" id="attachOrderModal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content res-modal res-modal--sm">
          <div class="res-modal__header">
            <div class="res-modal__header-left">
              <span class="res-modal__eyebrow">Billing</span>
              <h2 class="res-modal__number">Attach Order</h2>
            </div>
            <div class="res-modal__header-right">
              <button class="icon-btn icon-btn--close" data-dismiss="modal"><i class="bi bi-x"></i></button>
            </div>
          </div>
          <div class="res-modal__body res-modal__body--pad">
            <p class="attach-hint">Search by order number to attach it to this reservation.</p>
            <form @submit.prevent="searchOrderByOrderNumber" class="search-form">
              <input type="text" class="search-input" v-model="searchOrderValue" placeholder="Order number…"/>
              <button type="submit" class="btn btn-primary btn-sm">Search</button>
            </form>
            <div v-if="orders.length" class="search-results">
              <div
                v-for="order in orders"
                :key="order.id"
                class="search-result-row"
                @click="attachOrderToReservation(order.id, reservation.reservation.id)"
              >
                <span class="srr-number">{{ order.order }}</span>
                <span class="srr-amount">{{ convertCurrencyToLocal(order.total_amount) }}</span>
                <span class="srr-date">{{ formatDate(order.created_at,'DD MMM YYYY') }}</span>
                <i class="bi bi-plus srr-icon"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════
         HIDDEN 80mm THERMAL SOURCE
         Never visible on screen — written to a print window
         ══════════════════════════════════════════ -->
    <div id="thermalReceipt" style="display:none" v-if="reservation && reservation.reservation">
      <div class="th-receipt">
        <div class="th-center">
          <div class="th-co-name">{{ companyInfo.name }}</div>
          <div class="th-co-meta" v-if="companyInfo.address">{{ companyInfo.address }}</div>
          <div class="th-co-meta" v-if="companyInfo.phone">Tel: {{ companyInfo.phone }}</div>
          <div class="th-co-meta" v-if="companyInfo.email">{{ companyInfo.email }}</div>
          <div class="th-co-meta" v-if="companyInfo.krapin">PIN: {{ companyInfo.krapin }}</div>
        </div>
        <div class="th-div">================================</div>
        <div class="th-center th-type">HOTEL RESERVATION RECEIPT</div>

        <div class="th-div">================================</div>
        <div class="th-row"><span>Receipt:</span><span>{{ receiptNumber }}</span></div>
        <div class="th-row"><span>Date:</span><span>{{ currentDateTime }}</span></div>
        <div class="th-div">================================</div>
        <div class="th-stitle">GUEST</div>
        <div class="th-row"><span>Name:</span><span>{{ reservation.reservation.customer?.full_name || '—' }}</span></div>
        <div class="th-row" v-if="reservation.reservation.customer?.phone">
          <span>Phone:</span><span>{{ reservation.reservation.customer.phone }}</span>
        </div>
        <div class="th-row" v-if="reservation.reservation.customer?.krapin">
          <span>PIN:</span><span>{{ reservation.reservation.customer.krapin }}</span>
        </div>
        <div class="th-div">--------------------------------</div>
        <div class="th-stitle">STAY DETAILS</div>
        <div class="th-row"><span>Ref #:</span><span>{{ reservation.reservation.reservation_number }}</span></div>
        <div class="th-row" v-if="reservation.room"><span>Room:</span><span>{{ reservation.room.product }}</span></div>
        <div class="th-row"><span>Status:</span><span>{{ reservation.status }}</span></div>
        <div class="th-row"><span>Check-in:</span><span>{{ formatDate(reservation.reservation.check_in,'DD MMM YYYY') }}</span></div>
        <div class="th-row"><span>Check-out:</span><span>{{ formatDate(reservation.reservation.check_out,'DD MMM YYYY') }}</span></div>
        <div class="th-row"><span>Nights:</span><span>{{ nightsCount }}</span></div>
        <div class="th-row">
          <span>Guests:</span>
          <span>{{ reservation.guests?.adults || 0 }} adults · {{ reservation.guests?.children || 0 }} children</span>
        </div>
        <template v-if="reservation.orders && reservation.orders.length">
          <div class="th-div">--------------------------------</div>
          <div class="th-stitle">ORDERS</div>
          <div v-for="o in reservation.orders" :key="o.id">
            <div class="th-row"><span>{{ o.order }}</span><span>{{ convertCurrencyToLocal(o.total_amount) }}</span></div>
          </div>
        </template>
        <div class="th-div">================================</div>
        <div class="th-stitle">PAYMENT</div>
        <div class="th-row"><span>Total:</span><span>{{ convertCurrencyToLocal(reservation.total_amount) }}</span></div>
        <div class="th-row"><span>Paid:</span><span>{{ convertCurrencyToLocal(reservation.amount_paid) }}</span></div>
        <div class="th-div-thin">- - - - - - - - - - - - - - - -</div>
        <div class="th-row th-bold"><span>Balance:</span><span>{{ convertCurrencyToLocal(reservation.balance) }}</span></div>
        <!-- Fiscal block injected by _thermalFiscalBlock() -->
        <div id="_thermal_fiscal_res"></div>
        <div class="th-div">================================</div>
        <div class="th-center">Thank you for choosing {{ companyInfo.name }}!</div>
        <div class="th-center th-keep">KEEP FOR YOUR RECORDS</div>
        <div class="th-center" v-if="companyInfo.companysettings">
          {{ companyInfo.companysettings.receipt_footer_message }}
        </div>
        <div class="th-div">================================</div>
        <div class="th-center th-sys">{{ currentDateTime }}</div>
        <div class="th-cut"></div>
      </div>
    </div>

    <!-- Booking actions: payment, extend, transfer, cancel, check-in/out -->
    <b-modal
      v-model="act.show"
      :title="actTitle"
      centered
      modal-class="mr-fullscreen-sm"
      :ok-title="actOkTitle"
      :ok-variant="act.type === 'cancel' ? 'danger' : 'primary'"
      cancel-title="Back"
      :busy="act.busy"
      @ok.prevent="submitAction"
      @hidden="afterAction"
    >
      <template v-if="act.r">
        <p class="text-secondary small mb-3">
          #{{ act.r.reservation_number }} · {{ act.roomName }} · {{ formatDate(act.r.check_in,'DD MMM') }} → {{ formatDate(act.r.check_out,'DD MMM YYYY') }}
        </p>

        <template v-if="act.type === 'payment'">
          <div class="form-group">
            <label>Amount</label>
            <input v-model.number="act.amount" type="number" min="1" step="any" class="form-control" />
            <small class="text-secondary">Balance: {{ convertCurrencyToLocal(act.balance) }}</small>
          </div>
          <div class="form-group mb-0">
            <label>Paid with</label>
            <select v-model="act.paymentTypeId" class="custom-select">
              <option :value="null" disabled>Choose payment method</option>
              <option v-for="m in paymentMethods" :key="m.id" :value="m.id">{{ m.payment_method }}</option>
            </select>
          </div>
        </template>

        <template v-else-if="act.type === 'extend'">
          <label>New check-out date</label>
          <date-range-picker
            v-model="act.newCheckOut"
            :single-date-picker="true"
            :auto-apply="true"
            :ranges="false"
            :append-to-body="true"
            :min-date="act.minCheckOut"
            class="w-100"
          >
            <template #input="picker">
              <span v-if="picker.startDate">{{ formatDate(picker.startDate,'ddd DD MMM YYYY') }}</span>
              <span v-else class="text-secondary">Pick a date</span>
            </template>
          </date-range-picker>
          <small class="text-secondary d-block mt-1">The extra nights are added at the room's rate.</small>
        </template>

        <template v-else-if="act.type === 'transfer'">
          <label>Move to room</label>
          <div v-if="act.loadingRooms" class="text-center py-3"><span class="spinner-border spinner-border-sm text-primary"></span></div>
          <div v-else-if="!act.rooms.length" class="text-secondary small">No other room is free for these dates.</div>
          <div v-else class="list-group">
            <button v-for="room in act.rooms" :key="room.id" type="button"
                    class="list-group-item list-group-item-action d-flex justify-content-between"
                    :class="{ active: act.toRoom === room.id }" @click="act.toRoom = room.id">
              <span>{{ room.product }}</span><span>{{ convertCurrencyToLocal(room.ppu) }}</span>
            </button>
          </div>
        </template>

        <template v-else-if="act.type === 'cancel'">
          <p class="mb-2">This releases the room. Payments already taken are not refunded automatically.</p>
          <label>Reason</label>
          <textarea v-model="act.reason" rows="2" class="form-control" placeholder="e.g. Guest called to cancel"></textarea>
        </template>

        <template v-else>
          <p class="mb-0">{{ act.type === 'checkin' ? 'Check this guest in now?' : 'Check this guest out now?' }}</p>
          <p v-if="act.type === 'checkout' && act.balance > 0" class="text-danger small mt-2 mb-0">
            This booking still owes {{ convertCurrencyToLocal(act.balance) }}. Take payment first, or check out anyway.
          </p>
        </template>

        <p v-if="act.error" class="text-danger small mt-3 mb-0">{{ act.error }}</p>
      </template>
    </b-modal>

  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker'
import { mapGetters } from 'vuex'

export default {
  name: 'HotelReservations',
  components: { DateRangePicker },

  data () {
    return {
      companyInfo: {},
      reservations: [],
      reservation: null,
      orders: [],
      searchOrderValue: '',
      paymentMethods: [],
      act: { show: false, type: null, id: null, r: null, busy: false, error: null, done: false, rooms: [], newCheckOut: { startDate: null, endDate: null } },
      loading: false,
      loadingReservationDetails: false,
      filters: {
        start_date: moment().startOf('month').format('YYYY-MM-DD'),
        end_date:   moment().endOf('month').format('YYYY-MM-DD'),
        payment_status: '',
        search: '',
      },
      autoApply: true,
      append_to_body: true,
      datepicker_opens: 'right',
      ranges: false,
      dateRange: {
        startDate: moment().startOf('month').toDate(),
        endDate:   moment().endOf('month').toDate(),
      },
    }
  },

  created () {
    // Plain instance property — NOT in data() so Vue 2 doesn't skip it.
    // Vue 2 refuses to proxy properties whose names start with _ onto the instance.
    this._imageMemCache = {}
    this._searchTimer = null
    this.fetchReservations()

    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('reservation-id')) {
      this.viewReservation(urlParams.get('reservation-id'))
    }
  },

  computed: {
    actTitle () {
      return { checkin: 'Check in', checkout: 'Check out', cancel: 'Cancel booking', payment: 'Add payment', extend: 'Extend stay', transfer: 'Move to another room' }[this.act.type] || ''
    },
    actOkTitle () {
      return { checkin: 'Check in', checkout: 'Check out', cancel: 'Cancel booking', payment: 'Record payment', extend: 'Extend', transfer: 'Move guest' }[this.act.type] || 'OK'
    },
    ...mapGetters(['userDetails']),
    receiptNumber () {
      const r = this.reservation?.reservation
      if (!r) return ''
      return `RES-${String(r.reservation_number || r.id || 'XXXX').substring(0, 8).toUpperCase()}`
    },
    currentDateTime () {
      return new Date().toLocaleString('en-KE', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      })
    },
    nightsCount () {
      if (!this.reservation?.reservation) return 0
      const ci = this.reservation.reservation.check_in
      const co = this.reservation.reservation.check_out
      if (!ci || !co) return 0
      return Math.max(1, moment(co).diff(moment(ci), 'days'))
    },
    canGenerateFiscalReceipt () {
      const ft = this.reservation?.reservation?.fiscal_transaction
      if (!ft) return true
      return ft.status === 'failed'
    },
    canIssueFiscalCreditNote () {
      const ft = this.reservation?.reservation?.fiscal_transaction
      if (!ft || ft.status !== 'completed') return false
      return ft.transaction_type !== 'credit_note'
    },
    canDownloadCreditNote () {
      const fts = this.reservation?.reservation?.fiscal_transactions
      if (!Array.isArray(fts)) return false
      return fts.some(ft => ft.transaction_type === 'credit_note' && ft.status === 'completed')
    },
  },

  watch: {
    dateRange: {
      deep: true,
      handler (val) {
        this.filters.start_date = val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : ''
        this.filters.end_date   = val.endDate   ? moment(val.endDate).format('YYYY-MM-DD')   : ''
      },
    },
    userDetails: {
      immediate: true,
      handler (val) { if (val?.company) this.companyInfo = val.company },
    },
  },

  methods: {
    // ── DATA ──────────────────────────────────────────────────────────────
    onSearchInput () {
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => this.fetchReservations(), 350)
    },

    async fetchReservations () {
      this.loading = true
      try {
        const res = await axios.get('/api/reservations', { params: this.filters })
        this.reservations = Array.isArray(res.data) ? res.data : []
      } finally { this.loading = false }
    },
    async viewReservation (id) {
      this.loadingReservationDetails = true
      $('#reservationModal').modal('show')
      try {
        const res = await axios.get(`/api/reservations/statement/${id}`)
        this.reservation = res.data
      } finally { this.loadingReservationDetails = false }
    },

    // ── RESERVATION ACTIONS ───────────────────────────────────────────────
    // Each action opens a small dialog (the old buttons posted no data, so
    // payment/extend/transfer always failed, and cancel had no confirmation).
    // The detail modal is a jQuery modal that grabs focus, so it's closed
    // while the dialog is open and reopened (refreshed) afterwards.
    checkInReservation  (id) { this.openAction('checkin', id) },
    checkOutReservation (id) { this.openAction('checkout', id) },
    cancelReservation   (id) { this.openAction('cancel', id) },
    addPaymentReservation (id) { this.openAction('payment', id) },
    extendReservation   (id) { this.openAction('extend', id) },
    transferReservation (id) { this.openAction('transfer', id) },

    openAction (type, id) {
      const r = this.reservation?.reservation
      if (!r) return
      const balance = Number(this.reservation.balance || 0)
      this.act = {
        show: true, type, id, r, busy: false, error: null, done: false,
        roomName: this.reservation.room?.product || '',
        balance,
        amount: balance > 0 ? balance : null,
        paymentTypeId: null,
        newCheckOut: { startDate: null, endDate: null },
        minCheckOut: moment(r.check_out).add(1, 'day').format('YYYY-MM-DD'),
        rooms: [], toRoom: null, loadingRooms: false,
        reason: '',
      }
      $('#reservationModal').modal('hide')
      if (type === 'payment' && !this.paymentMethods.length) this.loadPaymentMethods()
      if (type === 'transfer') this.loadTransferRooms(r)
    },

    async loadPaymentMethods () {
      try {
        const { data } = await axios.get('/api/payments')
        this.paymentMethods = Array.isArray(data) ? data : []
      } catch (e) { this.paymentMethods = [] }
    },

    async loadTransferRooms (r) {
      this.act.loadingRooms = true
      try {
        const from = moment.max(moment(r.check_in), moment().startOf('day')).format('YYYY-MM-DD')
        const { data } = await axios.get('/api/reservations/availability', {
          params: { check_in: from, check_out: moment(r.check_out).format('YYYY-MM-DD'), adults: r.adults || 1, children: r.children || 0, all_rooms: 0 },
        })
        this.act.rooms = (Array.isArray(data) ? data : []).filter(room => room.id !== r.product_id)
      } catch (e) {
        this.act.rooms = []
      } finally {
        this.act.loadingRooms = false
      }
    },

    async submitAction () {
      const a = this.act
      a.error = null
      const endpoints = {
        checkin:  [`/api/reservations/check-in/${a.id}`, {}],
        checkout: [`/api/reservations/check-out/${a.id}`, {}],
        cancel:   [`/api/reservations/cancel/${a.id}`, { reason: a.reason }],
        payment:  [`/api/reservations/payment/${a.id}`, { amount: a.amount, payment_type_id: a.paymentTypeId }],
        extend:   [`/api/reservations/extend/${a.id}`, { new_check_out: a.newCheckOut.startDate ? moment(a.newCheckOut.startDate).format('YYYY-MM-DD') : null }],
        transfer: [`/api/reservations/transfer/${a.id}`, { to_product_id: a.toRoom }],
      }
      if (a.type === 'payment' && (!(a.amount > 0) || !a.paymentTypeId)) { a.error = 'Enter an amount and choose how it was paid.'; return }
      if (a.type === 'extend' && !a.newCheckOut.startDate) { a.error = 'Pick the new check-out date.'; return }
      if (a.type === 'transfer' && !a.toRoom) { a.error = 'Choose a room.'; return }
      if (a.type === 'cancel' && !a.reason.trim()) { a.error = 'Add a short reason.'; return }

      a.busy = true
      try {
        const [url, body] = endpoints[a.type]
        await axios.post(url, body)
        a.done = true
        a.show = false
        const done = { checkin: 'Guest checked in.', checkout: 'Guest checked out.', cancel: 'Booking cancelled and room released.',
                       payment: 'Payment recorded.', extend: 'Stay extended.', transfer: 'Guest moved to the new room.' }[a.type]
        this.$bvToast?.toast(done, { title: 'Done', variant: 'primary', solid: true })
        this.fetchReservations()
      } catch (e) {
        const errors = e.response?.data?.errors
        a.error = errors ? Object.values(errors).flat()[0] : (e.response?.data?.message || 'That did not work. Please try again.')
      } finally {
        a.busy = false
      }
    },

    // Back to the (refreshed) booking, unless it was cancelled.
    afterAction () {
      const a = this.act
      if (!a.id) return
      if (a.done && a.type === 'cancel') {
        this.reservations = this.reservations.filter(r => r.id !== a.id)
        return
      }
      this.viewReservation(a.id)
    },
    async generateFiscalReceiptReservation (id) {
      try {
        const rv = this.reservation?.reservation
        const type = rv?.customer?.krapin ? 'invoice' : 'sale'
        await axios.post(`/api/reservations/fiscal-receipt/${id}`, { transaction_type: type })
        this.$bvToast?.toast('Fiscal receipt queued for processing.', { title: 'Fiscal', variant: 'success', solid: true })
        this.viewReservation(id)
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed to generate fiscal receipt.'
        this.$bvToast?.toast(msg, { title: 'Error', variant: 'danger', solid: true })
      }
    },
    async fiscalCreditNoteReservation (id) {
      try {
        await axios.post(`/api/reservations/fiscal-credit-note/${id}`)
        this.$bvToast?.toast('Fiscal credit note queued for processing.', { title: 'Credit Note', variant: 'success', solid: true })
        this.viewReservation(id)
      } catch (e) {
        const msg = e.response?.data?.message || e.response?.data?.errors?.fiscal?.[0] || 'Failed to issue fiscal credit note.'
        this.$bvToast?.toast(msg, { title: 'Error', variant: 'danger', solid: true })
      }
    },
    openAttachOrderModal (id) {
      this.orders = []
      this.searchOrderValue = ''
      if (this.reservation?.reservation) this.reservation.reservation.id = id
      $('#attachOrderModal').modal('show')
    },
    async searchOrderByOrderNumber () {
      const res = await axios.get('/api/allorders', { params: { search: this.searchOrderValue } })
      this.orders = res.data?.orders?.data || []
    },
    async attachOrderToReservation (orderId, reservationId) {
      await axios.post(`/api/reservations/attach-order/${reservationId}`, { final_order_id: orderId })
      $('#attachOrderModal').modal('hide')
      this.viewReservation(reservationId)
    },
    async detachOrderFromReservation (reservationId, orderId) {
      await axios.post(`/api/reservations/detach-order/${reservationId}`, { final_order_id: orderId })
      this.viewReservation(reservationId)
    },
    openOrderDetails (orderId) { window.location.href = `/orders?order-number=${orderId}` },

    // ── 80mm THERMAL PRINT ────────────────────────────────────────────────
    // ── IMAGE PRELOAD (mirrors Invoice.vue) ─────────────────────────────
    _preloadImage (url) {
      // Return cached data URL immediately if already fetched (same pattern as Invoice.vue)
      if (this._imageMemCache[url]) {
        return Promise.resolve(this._imageMemCache[url])
      }
      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest()
        xhr.open('GET', url, true)
        xhr.responseType = 'blob'
        xhr.onload = () => {
          const reader = new FileReader()
          reader.onloadend = () => {
            this._imageMemCache[url] = reader.result // store in cache
            resolve(reader.result)
          }
          reader.onerror   = () => resolve(url)
          reader.readAsDataURL(xhr.response)
        }
        xhr.onerror = () => resolve(url)
        xhr.send()
      })
    },

    async printThermalReceipt () {
      if (!this.reservation) return
      const el = document.getElementById('thermalReceipt')
      if (!el) return
      const ft = this.reservation?.reservation?.fiscal_transaction || {}
      let qrDataUrl = null
      if (ft.qr_url) {
        const proxied = `/api/proxy-logo?remote_url=${encodeURIComponent(ft.qr_url)}`
        qrDataUrl = await this._preloadImage(proxied)
      }
      const fiscalEl = document.getElementById('_thermal_fiscal_res')
      if (fiscalEl) fiscalEl.innerHTML = this._thermalFiscalBlock(qrDataUrl)
      el.style.display = 'block'
      const pw = window.open('', '_blank')
      pw.document.write(`<!DOCTYPE html><html><head><title>Reservation Receipt</title><style>
@media print{@page{size:80mm auto;margin:0}body{margin:0;padding:0}}
body{font-family:Arial,sans-serif;font-size:13px;line-height:1.5;color:#000;background:#fff;margin:0;padding:10px;width:80mm}
.th-receipt{width:100%}
.th-center{text-align:center}
.th-co-name{font-size:15px;font-weight:700;margin-bottom:3px}
.th-co-meta{font-size:11px;margin-bottom:2px}
.th-type{font-size:13px;font-weight:700;margin:5px 0;letter-spacing:.5px}
.th-div{margin:5px 0;font-size:11px}
.th-div-thin{margin:3px 0;font-size:10px}
.th-stitle{font-weight:700;font-size:11px;margin:6px 0 3px;text-transform:uppercase;letter-spacing:.5px}
.th-row{display:flex;justify-content:space-between;margin-bottom:3px;font-size:12px}
.th-bold{font-weight:700;font-size:13px}
.th-keep{font-weight:700;margin:4px 0;font-size:12px}
.th-sys{font-size:10px;color:#666;margin-top:5px}
.th-cut{border-top:1px dashed #000;margin-top:18px}
.th-fiscal{margin:7px 0;padding:5px;border:1px solid #000;font-size:11px}
.th-fiscal-title{font-weight:700;font-size:11px;margin-bottom:4px;text-transform:uppercase}
.th-qr{text-align:center;margin:7px 0}
.th-qr img{width:86px;height:86px}
</style></head><body>${el.innerHTML}</body></html>`)
      pw.document.close()
      setTimeout(() => { pw.focus(); pw.print(); pw.close(); el.style.display = 'none' }, 250)
    },

    _thermalFiscalBlock (qrDataUrl = null) {
      const ft = this.reservation?.reservation?.fiscal_transaction
      if (!ft || (!ft.control_serial && !ft.control_unit_info)) return ''
      let h = `<div class="th-div">================================</div>
<div class="th-fiscal"><div class="th-fiscal-title">Fiscal / TIMS</div>`
      if (ft.control_datetime)        h += `<div class="th-row"><span>Date:</span><span>${this._esc(ft.control_datetime)}</span></div>`
      if (ft.control_invoice_number)  h += `<div class="th-row"><span>Invoice #:</span><span>${this._esc(ft.control_invoice_number)}</span></div>`
      if (ft.control_serial)          h += `<div class="th-row"><span>Serial:</span><span>${this._esc(ft.control_serial)}</span></div>`
      if (ft.control_unit_info)       h += `<div class="th-row"><span>CU Info:</span><span>${this._esc(ft.control_unit_info)}</span></div>`
      h += `</div>`
      if (ft.qr_url) {
        const imgSrc = qrDataUrl || `/api/proxy-logo?remote_url=${encodeURIComponent(ft.qr_url)}`
        h += `<div class="th-qr"><img src="${imgSrc}" alt="QR"/></div>`
      }
      return h
    },

    // ── A4 PDF ────────────────────────────────────────────────────────────
    async downloadPdfReceipt () {

      if (!this.reservation) return
      const ft = this.reservation?.reservation?.fiscal_transaction || {}
      let qrDataUrl = null
      if (ft.qr_url) {
        const proxied = `/api/proxy-logo?remote_url=${encodeURIComponent(ft.qr_url)}`
        qrDataUrl = await this._preloadImage(proxied)
      }
      const win = window.open('', '_blank')
      win.document.write(this._buildPdfHtml(qrDataUrl))
      win.document.close()
      setTimeout(() => { win.focus(); win.print() }, 600)
    },

    async downloadCreditNotePdf () {
      if (!this.reservation) return
      const fts = this.reservation?.reservation?.fiscal_transactions || []
      const cnFt = fts.find(ft => ft.transaction_type === 'credit_note' && ft.status === 'completed') || {}
      let qrDataUrl = null
      if (cnFt.qr_url) {
        const proxied = `/api/proxy-logo?remote_url=${encodeURIComponent(cnFt.qr_url)}`
        qrDataUrl = await this._preloadImage(proxied)
      }
      const win = window.open('', '_blank')
      win.document.write(this._buildCreditNotePdfHtml(cnFt, qrDataUrl))
      win.document.close()
      setTimeout(() => { win.focus(); win.print() }, 600)
    },

    _buildCreditNotePdfHtml (cnFt = {}, qrDataUrl = null) {
      const rv  = this.reservation.reservation
      const co  = this.companyInfo
      const cu  = rv.customer || {}
      const rm  = this.reservation.room || {}
      const gs  = this.reservation.guests || {}
      const cfg = co.companysettings || {}
      const nights = this.nightsCount
      const fts = rv.fiscal_transactions || []
      const origFt = fts.find(ft => ['sale', 'invoice'].includes(ft.transaction_type)) || {}

      const hasFiscal = !!(cnFt.control_serial || cnFt.control_unit_info)

      const creditNoteSection = hasFiscal ? `
        <div style="border:1.5pt solid #b91c1c;border-radius:4pt;margin-bottom:16pt;overflow:hidden">
          <div style="background:#b91c1c;color:#fff;padding:7pt 12pt;font-size:7.5pt;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;display:flex;align-items:center;gap:6pt">
            <span>⊖</span><span>Fiscal Credit Note / TIMS Verification</span>
          </div>
          <div style="padding:10pt 12pt">
            ${origFt.control_invoice_number ? `<div style="display:flex;justify-content:space-between;padding:3pt 0;border-bottom:.5pt solid #fee2e2"><span style="color:#9f1239;font-size:8pt;font-weight:600">Original Invoice Reference</span><span style="font-family:'Courier New',monospace;font-size:8pt;font-weight:700;color:#b91c1c">${this._esc(origFt.control_invoice_number)}</span></div>` : ''}
            ${cnFt.control_datetime       ? `<div style="display:flex;justify-content:space-between;padding:3pt 0;border-bottom:.5pt solid #fee2e2"><span style="color:#64748b;font-size:8pt">Credit Note Date</span><span style="font-family:'Courier New',monospace;font-size:8pt;font-weight:700">${this._esc(cnFt.control_datetime)}</span></div>` : ''}
            ${cnFt.control_invoice_number ? `<div style="display:flex;justify-content:space-between;padding:3pt 0;border-bottom:.5pt solid #fee2e2"><span style="color:#64748b;font-size:8pt">Credit Note Number</span><span style="font-family:'Courier New',monospace;font-size:8pt;font-weight:700">${this._esc(cnFt.control_invoice_number)}</span></div>` : ''}
            ${cnFt.control_serial         ? `<div style="display:flex;justify-content:space-between;padding:3pt 0;border-bottom:.5pt solid #fee2e2"><span style="color:#64748b;font-size:8pt">Control Serial</span><span style="font-family:'Courier New',monospace;font-size:8pt;font-weight:700">${this._esc(cnFt.control_serial)}</span></div>` : ''}
            ${cnFt.control_unit_info      ? `<div style="display:flex;justify-content:space-between;padding:3pt 0"><span style="color:#64748b;font-size:8pt">Control Unit Info</span><span style="font-family:'Courier New',monospace;font-size:8pt;font-weight:700">${this._esc(cnFt.control_unit_info)}</span></div>` : ''}
          </div>
          ${cnFt.qr_url ? `<div style="border-top:.75pt solid #fecaca;padding:10pt;text-align:center"><img src="${qrDataUrl || `/api/proxy-logo?remote_url=${encodeURIComponent(cnFt.qr_url)}`}" style="width:68pt;height:68pt" alt="QR"/><p style="font-size:6.5pt;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-top:4pt">Scan to verify with KRA</p></div>` : ''}
        </div>` : ''

      return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/>
<title>Credit Note – ${this._esc(rv.reservation_number)}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
@page{size:A4;margin:13mm 15mm}
body{font-family:'DM Sans',sans-serif;font-size:9.5pt;color:#1a1a2e;background:#fff;line-height:1.55}
.doc{max-width:780px;margin:0 auto}
.lh{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:12pt;border-bottom:2pt solid #b91c1c;margin-bottom:14pt}
.co-name{font-family:'DM Serif Display',Georgia,serif;font-size:20pt;color:#1a1a2e;margin-bottom:4pt}
.co-meta{font-size:8.5pt;color:#64748b;line-height:1.7}
.co-meta b{color:#1a1a2e}
.pin-mono{font-family:'Courier New',monospace;font-size:8pt;background:#f1f5f9;border:.5pt solid #cbd5e1;border-radius:3pt;padding:1pt 5pt}
.lh-r{text-align:right;flex-shrink:0;padding-left:24pt}
.ey{font-size:6.5pt;text-transform:uppercase;letter-spacing:2px;color:#b91c1c;display:block;margin-bottom:3pt;font-weight:700}
.rn{font-family:'Courier New',monospace;font-size:14pt;font-weight:700;color:#b91c1c;display:block}
.rd{font-size:8pt;color:#64748b;margin-top:3pt;display:block}
.cn-badge{display:inline-block;margin-top:7pt;padding:3pt 11pt;border-radius:20pt;font-size:7.5pt;font-weight:700;text-transform:uppercase;letter-spacing:.4px;color:#fff;background:#b91c1c}
.cn-notice{background:#fef2f2;border:1pt solid #fecaca;border-radius:4pt;padding:8pt 12pt;margin-bottom:14pt;font-size:8.5pt;color:#9f1239}
.cn-notice b{font-weight:700}
.sec-div{display:flex;align-items:center;gap:10pt;margin:12pt 0 9pt}
.sec-div-lbl{font-size:6.5pt;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;font-weight:700;white-space:nowrap}
.sec-div::after{content:'';flex:1;height:.75pt;background:#e2e8f0}
.stay-bar{display:flex;background:#f8fafc;border:.75pt solid #e2e8f0;border-radius:4pt;margin-bottom:14pt;overflow:hidden}
.sb{flex:1;padding:8pt 12pt;border-right:.5pt solid #e2e8f0}
.sb:last-child{border-right:none}
.sb-l{font-size:6.5pt;text-transform:uppercase;letter-spacing:1.2px;color:#94a3b8;margin-bottom:2pt}
.sb-v{font-size:9.5pt;font-weight:600;color:#1a1a2e}
.panels{display:flex;gap:12pt;margin-bottom:14pt}
.panel{flex:1;border:.75pt solid #e2e8f0;border-radius:4pt;padding:10pt 12pt}
.pttl{font-size:6.5pt;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;font-weight:700;border-bottom:.75pt solid #f1f5f9;padding-bottom:5pt;margin-bottom:8pt}
.fld{margin-bottom:5pt}
.fld .k{font-size:7pt;color:#94a3b8;text-transform:uppercase;letter-spacing:.3px;display:block;margin-bottom:1pt}
.fld .v{font-size:9pt;color:#1a1a2e;font-weight:500}
.pay-r{display:flex;justify-content:flex-end;margin-bottom:14pt}
.pay-box{width:44%;border:1pt solid #fecaca;border-radius:4pt;overflow:hidden}
.pay-box table{width:100%;border-collapse:collapse}
.pay-box td{padding:5pt 10pt;font-size:9pt}
.pay-box td.pl{color:#64748b}
.pay-box td.pr{text-align:right;font-family:'Courier New',monospace}
.pay-box tr.grand td{border-top:1.5pt solid #b91c1c;padding:6pt 10pt;font-size:11pt;font-weight:700;color:#b91c1c}
.doc-foot{border-top:.75pt solid #e2e8f0;padding-top:10pt;display:flex;justify-content:space-between;align-items:flex-end}
.df-l{font-size:8pt;color:#64748b}
.df-l .ty{font-family:'DM Serif Display',Georgia,serif;font-size:11pt;color:#b91c1c;margin-bottom:3pt}
.df-r{font-size:7.5pt;color:#94a3b8;text-align:right;font-family:'Courier New',monospace}
@media print{body{background:#fff}@page{size:A4;margin:13mm 15mm}}
</style></head><body><div class="doc">

<div class="lh">
  <div>
    <div class="co-name">${this._esc(co.name||'')}</div>
    <div class="co-meta">
      ${co.address  ? `<div>${this._esc(co.address)}</div>` : ''}
      ${co.building ? `<div>${this._esc(co.building)}</div>` : ''}
      ${co.town     ? `<div>${this._esc(co.town)}</div>` : ''}
      ${co.phone    ? `<div><b>Tel:</b> ${this._esc(co.phone)}</div>` : ''}
      ${co.email    ? `<div><b>Email:</b> ${this._esc(co.email)}</div>` : ''}
      ${co.krapin   ? `<div><b>PIN:</b> <span class="pin-mono">${this._esc(co.krapin)}</span></div>` : ''}
    </div>
  </div>
  <div class="lh-r">
    <span class="ey">Fiscal Credit Note</span>
    <span class="rn">${this._esc(rv.reservation_number||'')}</span>
    <span class="rd">Issued ${this.currentDateTime}</span>
    <span class="cn-badge">Credit Note</span>
  </div>
</div>

<div class="cn-notice">
  This document cancels and reverses the charges for reservation <b>${this._esc(rv.reservation_number||'')}</b>.
  ${origFt.control_invoice_number ? `Original fiscal invoice: <b>${this._esc(origFt.control_invoice_number)}</b>.` : ''}
  The room has been released and is available for rebooking.
</div>

<div class="sec-div"><span class="sec-div-lbl">Stay Overview</span></div>
<div class="stay-bar">
  <div class="sb"><div class="sb-l">Room / House</div><div class="sb-v">${this._esc(rm.product||'—')}</div></div>
  <div class="sb"><div class="sb-l">Check-in</div><div class="sb-v">${this.formatDate(rv.check_in,'DD MMM YYYY')}</div></div>
  <div class="sb"><div class="sb-l">Check-out</div><div class="sb-v">${this.formatDate(rv.check_out,'DD MMM YYYY')}</div></div>
  <div class="sb"><div class="sb-l">Nights</div><div class="sb-v">${nights}</div></div>
  <div class="sb"><div class="sb-l">Guests</div><div class="sb-v">${gs.adults||0} adults · ${gs.children||0} children</div></div>
</div>

<div class="sec-div"><span class="sec-div-lbl">Details</span></div>
<div class="panels">
  <div class="panel">
    <div class="pttl">Guest Information</div>
    ${cu.full_name ? `<div class="fld"><span class="k">Name</span><span class="v">${this._esc(cu.full_name)}</span></div>` : ''}
    ${cu.phone     ? `<div class="fld"><span class="k">Phone</span><span class="v">${this._esc(cu.phone)}</span></div>` : ''}
    ${cu.email     ? `<div class="fld"><span class="k">Email</span><span class="v">${this._esc(cu.email)}</span></div>` : ''}
    ${cu.krapin    ? `<div class="fld"><span class="k">KRA PIN</span><span class="pin-mono">${this._esc(cu.krapin)}</span></div>` : ''}
  </div>
  <div class="panel">
    <div class="pttl">Reservation Details</div>
    <div class="fld"><span class="k">Reference</span><span class="v" style="font-family:'Courier New',monospace">${this._esc(rv.reservation_number||'')}</span></div>
    ${rm.product ? `<div class="fld"><span class="k">Room / House</span><span class="v">${this._esc(rm.product)}</span></div>` : ''}
    <div class="fld"><span class="k">Check-in</span><span class="v">${this.formatDate(rv.check_in,'DD MMM YYYY')}</span></div>
    <div class="fld"><span class="k">Check-out</span><span class="v">${this.formatDate(rv.check_out,'DD MMM YYYY')}</span></div>
    <div class="fld"><span class="k">Duration</span><span class="v">${nights} night${nights!==1?'s':''}</span></div>
    ${origFt.control_invoice_number ? `<div class="fld"><span class="k">Original Invoice Ref</span><span class="v" style="font-family:'Courier New',monospace;color:#b91c1c">${this._esc(origFt.control_invoice_number)}</span></div>` : ''}
  </div>
</div>

<div class="sec-div"><span class="sec-div-lbl">Credit Summary</span></div>
<div class="pay-r">
  <div class="pay-box">
    <table>
      <tr><td class="pl">Reservation Total</td><td class="pr">${this._esc(this.convertCurrencyToLocal(this.reservation.total_amount))}</td></tr>
      <tr class="grand"><td class="pl">Credit Amount</td><td class="pr">−${this._esc(this.convertCurrencyToLocal(this.reservation.total_amount))}</td></tr>
    </table>
  </div>
</div>

${creditNoteSection}

<div class="doc-foot">
  <div class="df-l">
    <div class="ty">Credit Note – ${this._esc(co.name||'')}</div>
    ${cfg.receipt_footer_message ? `<div>${this._esc(cfg.receipt_footer_message)}</div>` : ''}
    <div style="margin-top:3pt">This credit note is issued as a reversal of the original tax invoice.</div>
  </div>
  <div class="df-r">
    <div>${this._esc(rv.reservation_number||'')}</div>
    <div>${this.currentDateTime}</div>
    ${co.krapin ? `<div>PIN: ${this._esc(co.krapin)}</div>` : ''}
  </div>
</div>

</div></body></html>`
    },

    _buildPdfHtml (qrDataUrl = null) {
      const rv  = this.reservation.reservation
      const co  = this.companyInfo
      const cu  = rv.customer || {}
      const rm  = this.reservation.room || {}
      const ft  = rv.fiscal_transaction || {}
      const gs  = this.reservation.guests || {}
      const ors = this.reservation.orders || []
      const cfg = co.companysettings || {}
      const nights = this.nightsCount
      const hasFiscal = !!(ft.control_serial || ft.control_unit_info)

      const statusPalette = {
        confirmed:   ['#0d6341','#e6f9f1'],
        pending:     ['#7a4f00','#fff8e6'],
        cancelled:   ['#8b0000','#ffeaea'],
        checked_in:  ['#1e3a8a','#e6eeff'],
        checked_out: ['#2c2c2c','#f0f0f0'],
      }
      const [sc, sbg] = statusPalette[this.reservation.status] || ['#374151','#f3f4f6']

      const orderRows = ors.map((o, i) => `
        <tr style="background:${i%2===0?'#fff':'#fafafa'}">
          <td style="font-family:'Courier New',monospace;font-size:9pt;padding:5pt 8pt;font-weight:600">${this._esc(o.order)}</td>
          <td style="font-size:8.5pt;padding:5pt 8pt;color:#64748b">${this._esc(this.formatDate(o.created_at,'DD MMM YYYY · HH:mm'))}</td>
          <td style="text-align:right;font-family:'Courier New',monospace;font-size:9pt;padding:5pt 8pt;font-weight:600">${this._esc(this.convertCurrencyToLocal(o.total_amount))}</td>
          <td style="padding:5pt 8pt">
            <span style="display:inline-block;padding:1.5pt 6pt;border-radius:20pt;font-size:7pt;font-weight:700;text-transform:uppercase;
              background:${o.status===1?'#e6f9f1':o.partial?'#fff8e6':'#ffeaea'};
              color:${o.status===1?'#0d6341':o.partial?'#7a4f00':'#8b0000'}">
              ${o.status===1?'Cleared':o.partial?'Partial':'Unpaid'}
            </span>
          </td>
        </tr>`).join('')

      const fiscalSection = hasFiscal ? `
        <div style="border:1pt solid #1a1a2e;border-radius:4pt;margin-bottom:16pt;overflow:hidden">
          <div style="background:#1a1a2e;color:#fff;padding:7pt 12pt;font-size:7.5pt;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;display:flex;align-items:center;gap:6pt">
            <span>🔒</span><span>Fiscal / TIMS Verification</span>
          </div>
          <div style="padding:10pt 12pt">
            ${ft.control_datetime       ? `<div style="display:flex;justify-content:space-between;padding:3pt 0;border-bottom:.5pt solid #f1f5f9"><span style="color:#64748b;font-size:8pt">Date &amp; Time</span><span style="font-family:'Courier New',monospace;font-size:8pt;font-weight:700">${this._esc(ft.control_datetime)}</span></div>` : ''}
            ${ft.control_invoice_number ? `<div style="display:flex;justify-content:space-between;padding:3pt 0;border-bottom:.5pt solid #f1f5f9"><span style="color:#64748b;font-size:8pt">Invoice Number</span><span style="font-family:'Courier New',monospace;font-size:8pt;font-weight:700">${this._esc(ft.control_invoice_number)}</span></div>` : ''}
            ${ft.control_serial         ? `<div style="display:flex;justify-content:space-between;padding:3pt 0;border-bottom:.5pt solid #f1f5f9"><span style="color:#64748b;font-size:8pt">Control Serial</span><span style="font-family:'Courier New',monospace;font-size:8pt;font-weight:700">${this._esc(ft.control_serial)}</span></div>` : ''}
            ${ft.control_unit_info      ? `<div style="display:flex;justify-content:space-between;padding:3pt 0"><span style="color:#64748b;font-size:8pt">Control Unit Info</span><span style="font-family:'Courier New',monospace;font-size:8pt;font-weight:700">${this._esc(ft.control_unit_info)}</span></div>` : ''}
          </div>
          ${ft.qr_url ? `<div style="border-top:.75pt solid #e2e8f0;padding:10pt;text-align:center"><img src="${qrDataUrl || `/api/proxy-logo?remote_url=${encodeURIComponent(ft.qr_url)}`}" style="width:68pt;height:68pt" alt="QR"/><p style="font-size:6.5pt;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-top:4pt">Scan to verify with KRA</p></div>` : ''}
        </div>` : ''

      return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/>
<title>Reservation Receipt – ${this._esc(rv.reservation_number)}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
@page{size:A4;margin:13mm 15mm}
body{font-family:'DM Sans',sans-serif;font-size:9.5pt;color:#1a1a2e;background:#fff;line-height:1.55}
.doc{max-width:780px;margin:0 auto}

.lh{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:12pt;border-bottom:2pt solid #1a1a2e;margin-bottom:14pt}
.co-name{font-family:'DM Serif Display',Georgia,serif;font-size:20pt;color:#1a1a2e;margin-bottom:4pt}
.co-meta{font-size:8.5pt;color:#64748b;line-height:1.7}
.co-meta b{color:#1a1a2e}
.pin-mono{font-family:'Courier New',monospace;font-size:8pt;background:#f1f5f9;border:.5pt solid #cbd5e1;border-radius:3pt;padding:1pt 5pt}
.lh-r{text-align:right;flex-shrink:0;padding-left:24pt}
.ey{font-size:6.5pt;text-transform:uppercase;letter-spacing:2px;color:#94a3b8;display:block;margin-bottom:3pt}
.rn{font-family:'Courier New',monospace;font-size:14pt;font-weight:700;color:#1a1a2e;display:block}
.rd{font-size:8pt;color:#64748b;margin-top:3pt;display:block}
.spill{display:inline-block;margin-top:7pt;padding:3pt 11pt;border-radius:20pt;font-size:7.5pt;font-weight:700;text-transform:uppercase;letter-spacing:.4px;color:${sc};background:${sbg}}

.sec-div{display:flex;align-items:center;gap:10pt;margin:12pt 0 9pt}
.sec-div-lbl{font-size:6.5pt;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;font-weight:700;white-space:nowrap}
.sec-div::after{content:'';flex:1;height:.75pt;background:#e2e8f0}

.stay-bar{display:flex;background:#f8fafc;border:.75pt solid #e2e8f0;border-radius:4pt;margin-bottom:14pt;overflow:hidden}
.sb{flex:1;padding:8pt 12pt;border-right:.5pt solid #e2e8f0}
.sb:last-child{border-right:none}
.sb-l{font-size:6.5pt;text-transform:uppercase;letter-spacing:1.2px;color:#94a3b8;margin-bottom:2pt}
.sb-v{font-size:9.5pt;font-weight:600;color:#1a1a2e}
.sb-v-g{color:#0d6341}

.panels{display:flex;gap:12pt;margin-bottom:14pt}
.panel{flex:1;border:.75pt solid #e2e8f0;border-radius:4pt;padding:10pt 12pt}
.pttl{font-size:6.5pt;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;font-weight:700;border-bottom:.75pt solid #f1f5f9;padding-bottom:5pt;margin-bottom:8pt}
.fld{margin-bottom:5pt}
.fld .k{font-size:7pt;color:#94a3b8;text-transform:uppercase;letter-spacing:.3px;display:block;margin-bottom:1pt}
.fld .v{font-size:9pt;color:#1a1a2e;font-weight:500}

table.ot{width:100%;border-collapse:collapse;margin-bottom:14pt}
table.ot thead th{background:#1a1a2e;color:#fff;font-size:7.5pt;font-weight:600;text-transform:uppercase;letter-spacing:.4px;padding:5pt 8pt}
table.ot thead th.r{text-align:right}
table.ot tbody td{border-bottom:.5pt solid #f1f5f9}

.pay-r{display:flex;justify-content:flex-end;margin-bottom:14pt}
.pay-box{width:44%;border:.75pt solid #e2e8f0;border-radius:4pt;overflow:hidden}
.pay-box table{width:100%;border-collapse:collapse}
.pay-box td{padding:5pt 10pt;font-size:9pt}
.pay-box td.pl{color:#64748b}
.pay-box td.pr{text-align:right;font-family:'Courier New',monospace}
.pay-box tr.sep td{border-top:.75pt solid #e2e8f0;padding-top:6pt}
.pay-box tr.grand td{border-top:1.5pt solid #1a1a2e;padding:6pt 10pt;font-size:11pt;font-weight:700}
.pay-box tr.paid td{color:#0d6341}
.pay-box tr.bal td{color:#b91c1c}

.doc-foot{border-top:.75pt solid #e2e8f0;padding-top:10pt;display:flex;justify-content:space-between;align-items:flex-end}
.df-l{font-size:8pt;color:#64748b}
.df-l .ty{font-family:'DM Serif Display',Georgia,serif;font-size:11pt;color:#1a1a2e;margin-bottom:3pt}
.df-r{font-size:7.5pt;color:#94a3b8;text-align:right;font-family:'Courier New',monospace}

@media print{body{background:#fff}@page{size:A4;margin:13mm 15mm}}
</style></head><body><div class="doc">

<div class="lh">
  <div>
    <div class="co-name">${this._esc(co.name||'')}</div>
    <div class="co-meta">
      ${co.address  ? `<div>${this._esc(co.address)}</div>` : ''}
      ${co.building ? `<div>${this._esc(co.building)}</div>` : ''}
      ${co.town     ? `<div>${this._esc(co.town)}</div>` : ''}
      ${co.phone    ? `<div><b>Tel:</b> ${this._esc(co.phone)}</div>` : ''}
      ${co.email    ? `<div><b>Email:</b> ${this._esc(co.email)}</div>` : ''}
      ${co.krapin   ? `<div><b>PIN:</b> <span class="pin-mono">${this._esc(co.krapin)}</span></div>` : ''}
    </div>
  </div>
  <div class="lh-r">
    <span class="ey">Hotel Reservation Receipt</span>
    <span class="rn">${this._esc(rv.reservation_number||'')}</span>
    <span class="rd">Issued ${this.currentDateTime}</span>
    <span class="spill">${this._esc(this.reservation.status||'')}</span>
  </div>
</div>

<div class="sec-div"><span class="sec-div-lbl">Stay Overview</span></div>
<div class="stay-bar">
  <div class="sb"><div class="sb-l">Room / House</div><div class="sb-v">${this._esc(rm.product||'—')}</div></div>
  <div class="sb"><div class="sb-l">Check-in</div><div class="sb-v">${this.formatDate(rv.check_in,'DD MMM YYYY')}</div></div>
  <div class="sb"><div class="sb-l">Check-out</div><div class="sb-v">${this.formatDate(rv.check_out,'DD MMM YYYY')}</div></div>
  <div class="sb"><div class="sb-l">Nights</div><div class="sb-v sb-v-g">${nights}</div></div>
  <div class="sb"><div class="sb-l">Guests</div><div class="sb-v">${gs.adults||0} adults · ${gs.children||0} children</div></div>
</div>

<div class="sec-div"><span class="sec-div-lbl">Details</span></div>
<div class="panels">
  <div class="panel">
    <div class="pttl">Guest Information</div>
    ${cu.full_name ? `<div class="fld"><span class="k">Name</span><span class="v">${this._esc(cu.full_name)}</span></div>` : ''}
    ${cu.phone     ? `<div class="fld"><span class="k">Phone</span><span class="v">${this._esc(cu.phone)}</span></div>` : ''}
    ${cu.email     ? `<div class="fld"><span class="k">Email</span><span class="v">${this._esc(cu.email)}</span></div>` : ''}
    ${cu.company   ? `<div class="fld"><span class="k">Company</span><span class="v">${this._esc(cu.company)}</span></div>` : ''}
    ${cu.krapin    ? `<div class="fld"><span class="k">KRA PIN</span><span class="pin-mono">${this._esc(cu.krapin)}</span></div>` : ''}
    ${cu.address_address ? `<div class="fld"><span class="k">Address</span><span class="v">${this._esc(cu.address_address)}</span></div>` : ''}
  </div>
  <div class="panel">
    <div class="pttl">Reservation Details</div>
    <div class="fld"><span class="k">Reference</span><span class="v" style="font-family:'Courier New',monospace">${this._esc(rv.reservation_number||'')}</span></div>
    ${rm.product ? `<div class="fld"><span class="k">Room / House</span><span class="v">${this._esc(rm.product)}</span></div>` : ''}
    <div class="fld"><span class="k">Check-in</span><span class="v">${this.formatDate(rv.check_in,'DD MMM YYYY')}</span></div>
    <div class="fld"><span class="k">Check-out</span><span class="v">${this.formatDate(rv.check_out,'DD MMM YYYY')}</span></div>
    <div class="fld"><span class="k">Duration</span><span class="v">${nights} night${nights!==1?'s':''}</span></div>
    <div class="fld"><span class="k">Adults / Children</span><span class="v">${gs.adults||0} / ${gs.children||0}</span></div>
  </div>
</div>

${ors.length ? `
<div class="sec-div"><span class="sec-div-lbl">Attached Orders</span></div>
<table class="ot">
  <thead><tr><th>Order #</th><th>Date</th><th class="r">Amount</th><th>Status</th></tr></thead>
  <tbody>${orderRows}</tbody>
</table>` : ''}

<div class="sec-div"><span class="sec-div-lbl">Payment Summary</span></div>
<div class="pay-r">
  <div class="pay-box">
    <table>
      <tr><td class="pl">Reservation Total</td><td class="pr">${this._esc(this.convertCurrencyToLocal(this.reservation.total_amount))}</td></tr>
      <tr class="paid"><td class="pl">Amount Paid</td><td class="pr">${this._esc(this.convertCurrencyToLocal(this.reservation.amount_paid))}</td></tr>
      <tr class="grand bal"><td class="pl">Balance Due</td><td class="pr">${this._esc(this.convertCurrencyToLocal(this.reservation.balance))}</td></tr>
    </table>
  </div>
</div>

${fiscalSection}

<div class="doc-foot">
  <div class="df-l">
    <div class="ty">Thank you for choosing ${this._esc(co.name||'us')}!</div>
    ${cfg.receipt_footer_message ? `<div>${this._esc(cfg.receipt_footer_message)}</div>` : ''}
    <div style="margin-top:3pt">Please retain this receipt for your records.</div>
  </div>
  <div class="df-r">
    <div>${this._esc(rv.reservation_number||this.receiptNumber)}</div>
    <div>${this.currentDateTime}</div>
    ${co.krapin ? `<div>PIN: ${this._esc(co.krapin)}</div>` : ''}
  </div>
</div>

</div></body></html>`
    },

    // ── HELPERS ───────────────────────────────────────────────────────────
    formatDate (date, format = 'DD-MM-YYYY') {
      return date ? moment(date).format(format) : '—'
    },
    convertCurrencyToLocal (amount) {
      return new Intl.NumberFormat().format(amount || 0)
    },
    guestInitial (customer) {
      if (!customer?.full_name) return '?'
      return customer.full_name.trim().charAt(0).toUpperCase()
    },
    _esc (str) {
      if (str == null) return ''
      return String(str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;')
    },
  },
}
</script>

<style>
/* b-modal content is teleported to <body>, outside scoped styles. */
@media (max-width: 575.98px) {
  .mr-fullscreen-sm .modal-dialog { margin: 0; max-width: 100%; min-height: 100%; }
  .mr-fullscreen-sm .modal-content { border: 0; border-radius: 0; min-height: 100vh; }
}
</style>

<style scoped>
/* ── DESIGN TOKENS ── */
.res-page {
  --primary:    #fd4719;
  --primary-bg: rgba(253,71,25,.1);
  --red-bg:     rgba(220,53,69,.1);
  --radius:     7px;
  padding: 0 0 56px;
}

/* ── BOOTSTRAP OVERRIDES ── */
.res-page .btn-primary,
.res-page .btn-primary:not(:disabled):not(.disabled) {
  background-color: #fd4719;
  border-color: #fd4719;
  color: #fff;
}
.res-page .btn-primary:hover,
.res-page .btn-primary:focus,
.res-page .btn-primary:active,
.res-page .btn-primary:not(:disabled):not(.disabled):active {
  background-color: #e03d14;
  border-color: #d03a12;
  color: #fff;
}
.res-page .btn-primary:focus,
.res-page .btn-primary:not(:disabled):not(.disabled):active:focus {
  box-shadow: 0 0 0 .2rem rgba(253,71,25,.35);
}

/* ── PAGE HEADER ── */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 32px 0 0;
}
.page-header__eyebrow {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--bs-secondary);
  margin-bottom: 5px;
  font-weight: 500;
}
.page-header__title {
  font-size: 26px;
  font-weight: 700;
  color: var(--bs-primary);
  margin: 0;
  letter-spacing: -0.5px;
}
.page-header__right { display: flex; align-items: center; gap: 10px; }

/* ── FILTER BAR ── */
.filter-bar {
  margin: 22px 0 0;
  border-bottom: 1px solid var(--bs-light);
  padding-bottom: 22px;
}
.filter-bar__inner { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.filter-slot { flex-shrink: 0; }
.filter-slot--date { flex: 0 0 290px; }
.filter-slot--search { flex: 1 1 180px; max-width: 260px; }
.search-input { width: 100%; }
.search-input::placeholder { color: var(--bs-secondary); }

.date-picker-trigger {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px;
  border: 1px solid var(--bs-light); border-radius: var(--radius);
  font-size: 13px; color: var(--bs-dark); background: #fff;
  cursor: pointer; min-width: 230px;
}
.date-picker-trigger .placeholder { color: var(--bs-secondary); }
.date-picker-trigger i { color: var(--bs-secondary); font-size: 13px; }

.filter-select {
  padding: 8px 12px;
  border: 1px solid var(--bs-light); border-radius: var(--radius);
  font-size: 13px; color: var(--bs-dark); background: #fff;
  appearance: none; -webkit-appearance: none;
  cursor: pointer; min-width: 160px;
}
.filter-select:focus { outline: none; border-color: var(--bs-dark); }

/* ── TABLE ── */
.table-wrap { margin-top: 10px; overflow-x: auto; }
.res-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.res-table thead th {
  padding: 10px 12px;
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.8px;
  color: var(--bs-secondary); border-bottom: 1px solid var(--bs-light); white-space: nowrap;
}
.res-table tbody .res-row { cursor: pointer; transition: background .1s; }
.res-table tbody .res-row:hover { background: var(--bs-light); }
.res-table tbody td {
  padding: 11px 12px; border-bottom: 1px solid #f1f5f9;
  color: var(--bs-dark); vertical-align: middle;
}
.cell-ref { font-size: 12px; color: var(--bs-secondary); }
.cell-guest { display: flex; align-items: center; gap: 10px; font-weight: 500; }
.guest-avatar {
  width: 30px; height: 30px; border-radius: 50%;
  background: var(--bs-dark); color: #fff;
  font-size: 12px; font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.cell-amount { font-size: 13px; }
.cell-date { color: var(--bs-secondary); font-size: 13px; }

/* ── STATUS CHIPS ── */
.status-chip {
  display: inline-block; padding: 3px 10px; border-radius: 20px;
  font-size: 11px; font-weight: 600; text-transform: capitalize; letter-spacing: 0.2px;
}
.status-chip--confirmed   { background: var(--primary-bg); color: var(--primary); }
.status-chip--pending     { background: rgba(220,53,69,.1); color: var(--bs-danger); }
.status-chip--cancelled   { background: rgba(220,53,69,.1); color: var(--bs-danger); }
.status-chip--checked_in  { background: var(--primary-bg); color: var(--primary); }
.status-chip--checked_out { background: rgba(108,117,125,.1); color: #6c757d; }
.status-chip--lg          { font-size: 12px; padding: 4px 13px; }

.empty-state {
  text-align: center; padding: 56px;
  color: var(--bs-secondary); font-size: 14px;
}
.empty-state i { font-size: 26px; display: block; margin-bottom: 12px; opacity: .5; }

/* ── RESERVATION MODAL ── */
.res-modal {
  border: none; border-radius: 12px; overflow: hidden;
  box-shadow: 0 24px 64px rgba(0,0,0,.15);
}
.modal-loading {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 72px; gap: 16px;
  color: var(--bs-secondary); font-size: 14px;
}
.spinner-ring {
  width: 36px; height: 36px;
  border: 3px solid #e2e8f0; border-top-color: var(--bs-dark);
  border-radius: 50%; animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.res-modal__header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px 24px 18px;
  border-bottom: 1px solid var(--bs-light); background: var(--bs-light);
}
.res-modal__eyebrow {
  display: block; font-size: 10px; text-transform: uppercase;
  letter-spacing: 1.5px; color: var(--bs-secondary); margin-bottom: 3px;
}
.res-modal__number { font-size: 19px; font-weight: 700; color: var(--bs-dark); margin: 0; letter-spacing: -0.3px; }
.res-modal__header-right { display: flex; align-items: center; gap: 8px; }

/* Stay strip */
.stay-strip {
  display: flex; align-items: center; flex-wrap: wrap;
  padding: 12px 24px; gap: 16px; background: var(--bs-dark);
}
.stay-strip__label {
  display: block; font-size: 9px; text-transform: uppercase;
  letter-spacing: 1.2px; color: rgba(255,255,255,.45); margin-bottom: 2px;
}
.text-primary { font-size: 13px; font-weight: 600; color: #fff; }
.text-primary--accent { color: rgba(255,255,255,.9); }
.stay-strip__sep { color: rgba(255,255,255,.2); font-size: 14px; }

/* Icon buttons */
.icon-btn {
  width: 33px; height: 33px; border-radius: 6px;
  border: 1px solid var(--bs-light); background: #fff;
  color: var(--bs-secondary);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 13px; cursor: pointer; transition: background .12s, color .12s;
}
.icon-btn:hover { background: var(--bs-light); color: var(--bs-dark); }
.icon-btn--close:hover { background: rgba(220,53,69,.1); color: var(--bs-danger); border-color: rgba(220,53,69,.3); }
.action-group { display: flex; align-items: center; gap: 6px; }

/* Dropdown */
.res-dropdown {
  border: 1px solid var(--bs-light); border-radius: var(--radius);
  box-shadow: 0 8px 28px rgba(0,0,0,.1); padding: 5px 0; min-width: 185px;
}
.dropdown-section-label {
  font-size: 10px; text-transform: uppercase; letter-spacing: 1px;
  color: var(--bs-secondary); padding: 7px 16px 3px; font-weight: 600;
}
.res-dropdown .dropdown-item {
  font-size: 13px; padding: 7px 16px; color: var(--bs-dark);
  display: flex; align-items: center; gap: 9px;
}
.res-dropdown .dropdown-item i { width: 14px; color: var(--bs-secondary); }
.res-dropdown .dropdown-item:hover { background: var(--bs-light); }
.res-dropdown .dropdown-item.text-danger { color: var(--bs-danger); }
.res-dropdown .dropdown-item.text-danger i { color: var(--bs-danger); }

/* Modal body layout */
.res-modal__body {
  display: flex;
}
.res-modal__main {
  flex: 1; padding: 20px 24px;
  border-right: 1px solid var(--bs-light);
  display: flex; flex-direction: column; gap: 16px;
}
.res-modal__body--pad { padding: 20px 24px; }
.res-modal__aside {
  width: 264px; flex-shrink: 0;
  padding: 20px 18px;
  background: var(--bs-light);
  display: flex; flex-direction: column; gap: 14px;
}

/* Detail cards — borrowed from SubscriptionDetails pattern */
.detail-card {
  border: 1px solid var(--bs-light); border-radius: var(--radius);
  overflow: hidden; background: #fff;
}
.detail-card__header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 9px 14px;
  border-bottom: 1px solid var(--bs-light); background: var(--bs-light);
}
.detail-card__label {
  font-size: 10px; text-transform: uppercase;
  letter-spacing: 1px; color: var(--bs-secondary); font-weight: 600;
}

/* Guest block */
.guest-block { display: flex; align-items: flex-start; gap: 14px; padding: 14px; }
.guest-block__avatar {
  width: 40px; height: 40px; border-radius: 50%;
  background: var(--bs-dark); color: #fff; font-size: 16px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.guest-block__name { font-size: 15px; font-weight: 600; color: var(--bs-dark); margin-bottom: 4px; }
.guest-block__meta {
  font-size: 12.5px; color: var(--bs-secondary);
  display: flex; align-items: center; gap: 7px; margin-bottom: 3px;
}
.guest-block__meta i { width: 12px; font-size: 11px; color: var(--bs-secondary); }

/* Orders list */
.order-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 14px; border-bottom: 1px solid #f8fafc; gap: 12px;
  transition: background .1s;
}
.order-row:last-child { border-bottom: none; }
.order-row:hover { background: var(--bs-light); }
.order-row__left  { flex: 1; min-width: 0; }
.order-row__right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.order-row__number {
  display: block; font-size: 13px; font-weight: 600;
  color: var(--bs-dark);
}
.order-row__date  { display: block; font-size: 11px; color: var(--bs-secondary); margin-top: 2px; }
.order-row__amount { font-size: 13px; font-weight: 600; color: var(--bs-dark); }

.order-chip {
  display: inline-block; padding: 2px 8px; border-radius: 12px;
  font-size: 10px; font-weight: 700; text-transform: uppercase;
}
.order-chip--cleared { background: var(--primary-bg); color: var(--primary); }
.order-chip--partial  { background: rgba(108,117,125,.1); color: #6c757d; }
.order-chip--unpaid   { background: rgba(220,53,69,.1); color: var(--bs-danger); }

.detach-btn {
  background: none; border: none; padding: 4px 6px;
  color: var(--bs-secondary); cursor: pointer; border-radius: 4px; font-size: 12px;
  transition: color .12s, background .12s;
}
.detach-btn:hover { color: var(--bs-danger); background: rgba(220,53,69,.1); }
.empty-text { padding: 16px 14px; font-size: 13px; color: var(--bs-secondary); margin: 0; }

/* Payment card */
.payment-stack { padding: 12px 14px 0; }
.payment-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 6px 0; font-size: 13px;
}
.payment-row__label { color: var(--bs-secondary); }
.payment-row__value { font-weight: 600; color: var(--bs-dark); }
.payment-row--paid     .payment-row__value { color: var(--primary); }
.payment-row--balance  .payment-row__value { color: var(--bs-danger); }
.payment-row__value--big { font-size: 16px; }
.payment-divider { height: 1px; background: var(--bs-light); margin: 4px 0; }

/* Stat chips */
.stat-chips { display: flex; gap: 8px; }
.stat-chip {
  flex: 1; border: 1px solid var(--bs-light); border-radius: var(--radius);
  padding: 11px 8px; text-align: center; background: #fff;
}
.stat-chip__val {
  display: block; font-size: 20px; font-weight: 700;
  color: var(--bs-dark); line-height: 1; margin-bottom: 4px;
}
.stat-chip__lbl {
  display: block; font-size: 10px; text-transform: uppercase;
  letter-spacing: 0.8px; color: var(--bs-secondary);
}

/* Attach modal */
.attach-hint { font-size: 13px; color: var(--bs-secondary); margin-bottom: 14px; }
.search-form { display: flex; gap: 10px; margin-bottom: 16px; }
.search-input {
  flex: 1; padding: 8px 12px;
  border: 1px solid var(--bs-light); border-radius: var(--radius);
  font-size: 13px; color: var(--bs-dark);
}
.search-input:focus { outline: none; border-color: var(--bs-dark); }
.search-results { display: flex; flex-direction: column; gap: 4px; }
.search-result-row {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 12px; border: 1px solid var(--bs-light); border-radius: var(--radius);
  cursor: pointer; transition: background .1s; font-size: 13px;
}
.search-result-row:hover { background: var(--bs-light); }
.srr-number { font-weight: 600; color: var(--bs-dark); flex: 1; }
.srr-amount { color: var(--bs-secondary); }
.srr-date   { color: var(--bs-secondary); font-size: 12px; }
.srr-icon   { color: var(--bs-secondary); margin-left: auto; }
.search-result-row:hover .srr-icon { color: var(--bs-dark); }

/* Hidden on screen */
#thermalReceipt { display: none !important; }

/* Modal width */
.modal-xl { max-width: 920px; }

/* ── PHONES: bookings as cards, detail modal full screen ── */
@media (max-width: 767.98px) {
  .page-header { flex-wrap: wrap; gap: 10px; }
  .page-header__title { font-size: 22px; }
  .filter-slot, .filter-slot--date, .filter-slot--search { flex: 1 1 100%; max-width: none; }
  .date-picker-trigger, .filter-select { min-width: 0; width: 100%; }

  .res-table thead { display: none; }
  .res-table, .res-table tbody { display: block; }
  .res-table tbody .res-row {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-areas: "guest amount" "room status" "dates dates";
    gap: 4px 10px;
    border: 1px solid var(--bs-light); border-radius: 10px;
    padding: 12px; margin-bottom: 10px;
  }
  .res-table tbody .res-row td { border: 0; padding: 0; }
  .res-row .cell-ref { display: none; }
  .res-row .cell-guest { grid-area: guest; min-width: 0; }
  .res-row .cell-amount { grid-area: amount; font-weight: 600; text-align: right; }
  .res-row .cell-room { grid-area: room; color: var(--bs-secondary); font-size: 13px; }
  .res-row .cell-status { grid-area: status; text-align: right; }
  .res-row .cell-date--in { grid-area: dates; font-size: 12px; }
  .res-row .cell-date--in::after { content: ' →'; }
  .res-row .cell-date--out { grid-area: dates; font-size: 12px; justify-self: end; }
  .res-table tbody tr:not(.res-row) { display: block; }

  .modal-xl { max-width: 100%; margin: 0; }
  .modal-dialog-centered { min-height: 100%; align-items: stretch; }
  .res-modal { border-radius: 0; min-height: 100vh; }
  .res-modal__header { flex-wrap: wrap; gap: 10px; padding: 14px 16px; }
  .res-modal__header-right { flex-wrap: wrap; }
  .stay-strip { flex-wrap: wrap; gap: 8px 12px; padding: 12px 16px; }
  .stay-strip__sep { display: none; }
  .res-modal__body { flex-direction: column; }
  .res-modal__main { border-right: 0; padding: 16px; }
  .res-modal__aside { width: auto; padding: 16px; }
  .res-modal__body--pad { padding: 16px; }
}
</style>
